import {organization_Edit_Api} from 'apis'
import {getId} from 'apis/model/base'
import {Select} from 'antd'
import {createValue} from 'components/form/utils'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useMemo} from 'react'
import {useActiveOrgTypeFields} from 'modules/local/useOrgTypeLabels'
import createEditableForm from 'views/AppLocals/factory/createEditableForm'
import {bindQueryParam} from 'views/AppLocals/functions/routerHelper'
import OrganizationContainerContext from 'views/AppLocals/Organization/OrganizationContainerContext'

const LabelPrivacyEditor = ({name, value, onChange}) => {
    const t = useTranslate()
    const {organization} = useContext(OrganizationContainerContext)
    const activeFields = useActiveOrgTypeFields(organization?.org_type)
    const parsedValue = typeof value === 'string' ? _.attempt(JSON.parse, value) : value
    const current = _.isError(parsedValue) ? {} : (parsedValue || {})

    if (!activeFields.length) {
        return (
            <p className="text-sm text-color-400 italic py-4">
                {t('No terminology configuration available for this organization type.')}
            </p>
        )
    }

    const handleFieldChange = (fieldKey, selected) => {
        onChange(createValue(name, JSON.stringify({...current, [fieldKey]: selected ?? null})))
    }

    return (
        <div className="flex flex-col gap-0 rounded-xl overflow-hidden border border-color-50">
            <div className="hidden md:grid grid-cols-3 gap-4 px-4 py-2 background-100">
                <span className="text-xs font-semibold text-color-300 uppercase tracking-wide">
                    {t('system label')}
                </span>
                <span className="text-xs font-semibold text-color-300 uppercase tracking-wide">
                    {t('custom display')}
                </span>
                <span className="text-xs font-semibold text-color-300 uppercase tracking-wide">
                    {t('context / description')}
                </span>
            </div>

            {activeFields.map((field, idx) => (
                <div
                    key={field.key}
                    className={[
                        'grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 px-4 py-3 items-center',
                        idx % 2 === 0 ? 'background' : 'background-100',
                    ].join(' ')}>
                    <div className="flex items-center gap-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-semibold background-200 text-color-400 border border-color-100">
                            {field.key}
                        </span>
                        <span className="text-sm font-medium text-color">
                            {field.label}
                        </span>
                    </div>

                    <Select
                        className="w-full"
                        value={current[field.key] || field.default || undefined}
                        placeholder={field.default || t('---Choose One---')}
                        allowClear
                        options={(field.options || []).map((opt) => ({
                            value: opt,
                            label: opt,
                        }))}
                        onChange={(v) => handleFieldChange(field.key, v)}
                    />

                    <span className="text-sm text-color-300 italic">
                        {field.description}
                    </span>
                </div>
            ))}
        </div>
    )
}

const formFields = [{name: 'label_privacy'}]

const formSchema = [
    {
        title: 'terminology & labels',
        children: [
            {
                name: 'label_privacy',
                component: LabelPrivacyEditor,
            },
        ],
    },
]

const OrganizationLabelSettings = () => {
    const {organization} = useContext(OrganizationContainerContext)

    const body = useMemo(() => {
        if (_.isEmpty(organization)) return null
        return createEditableForm(
            formFields,
            formSchema
        )({
            item: organization,
            query: bindQueryParam({id: getId(organization)}),
            apiInfo: organization_Edit_Api,
        })
    }, [organization])

    return body
}

export default OrganizationLabelSettings
